/**
 * Authentication Store - Zustand state management with Firebase
 * Following Meta patterns: simple, typed, performant
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User as FirebaseUser } from 'firebase/auth';
import type { UserProfile } from '@/services/authService';
import type { APIKey } from '@/types';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  apiKey: string | null;
  currentAPIKey: APIKey | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setFirebaseUser: (user: FirebaseUser | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setAPIKey: (key: string | null) => void;
  setCurrentAPIKey: (key: APIKey | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      firebaseUser: null,
      userProfile: null,
      apiKey: null,
      currentAPIKey: null,
      isAuthenticated: false,
      isLoading: true,
      setFirebaseUser: (firebaseUser) => set({ 
        firebaseUser, 
        isAuthenticated: !!firebaseUser 
      }),
      setUserProfile: (userProfile) => set({ userProfile }),
      setAPIKey: (apiKey) => {
        set({ apiKey });
        // Also update API client
        if (apiKey) {
          import('@/api/client').then(({ apiClient }) => {
            apiClient.setAPIKey(apiKey);
          });
        }
      },
      setCurrentAPIKey: (currentAPIKey) => set({ currentAPIKey }),
      setLoading: (isLoading) => set({ isLoading }),
      logout: () => {
        set({
          firebaseUser: null,
          userProfile: null,
          apiKey: null,
          currentAPIKey: null,
          isAuthenticated: false,
        });
        localStorage.removeItem('amebogpt_api_key');
      },
    }),
    {
      name: 'amebogpt-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        apiKey: state.apiKey,
        currentAPIKey: state.currentAPIKey,
      }),
    }
  )
);

